import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, Gauge, MessageSquarePlus, OctagonAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { useApi } from "@/hooks/use-api";
import { PageHeader, SectionTitle } from "@/components/common/PageHeader";
import { KpiCard, KpiStrip } from "@/components/common/KpiCard";
import { ErrorState } from "@/components/common/EmptyState";
import { fmtDayTime, fmtInt, fmtMW, fmtMWh, fmtNum } from "@/lib/format";

export default function AdminPage() {
  const navigate = useNavigate();
  const { setActiveCustomerId } = useAuth();
  const { data, isLoading, isError, refetch } = useApi("/admin/overview", {}, { refetchInterval: 120_000 });
  if (isError) return <ErrorState message="Kunne ikke hente Proxima-oversikten." onRetry={refetch} />;
  const t = data?.totals;
  const viewAs = (c) => {
    setActiveCustomerId(c.id);
    navigate("/");
  };
  return (
    <div className="space-y-7" data-testid="admin-page">
      <PageHeader eyebrow="Proxima" title="Alle kunder" description={data ? `Driftsdata oppdatert ${fmtDayTime(data.as_of)} · ${t.customers} kunder · ${t.plants} kraftverk` : ""} />
      <KpiStrip cols={4}>
        <KpiCard label="Kunder" value={t ? `${t.customers}` : ""} sub={t ? `${t.plants} kraftverk totalt` : ""} icon={Building2} loading={isLoading} testId="admin-kpi-customers" />
        <KpiCard label="Produksjon nå" value={t ? fmtNum(t.power_now_mw, 1) : ""} unit="MW" sub={t ? `av ${fmtNum(t.capacity_mw, 1)} MW installert` : ""} icon={Gauge} loading={isLoading} testId="admin-kpi-power" />
        <KpiCard label="Åpne hendelser" value={t ? `${t.events_open}` : ""} sub="på tvers av kunder" icon={OctagonAlert} loading={isLoading} testId="admin-kpi-events" />
        <KpiCard label="Åpne henvendelser" value={t ? `${t.service_requests_open}` : ""} sub="til Proxima service" icon={MessageSquarePlus} loading={isLoading} testId="admin-kpi-requests" />
      </KpiStrip>
      <section className="surface surface-pad">
        <SectionTitle title="Kundeporteføljer" description="Velg «Vis som kunde» for å se portalen slik kunden ser den" />
        {isLoading ? (
          <Skeleton className="mt-4 h-48 w-full" />
        ) : (
          <div className="mt-3 overflow-x-auto scroll-thin">
            <Table data-testid="admin-customers-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Kunde</TableHead>
                  <TableHead className="text-right">Kraftverk</TableHead>
                  <TableHead className="text-right">I drift</TableHead>
                  <TableHead className="text-right">Effekt nå</TableHead>
                  <TableHead className="text-right">I dag</TableHead>
                  <TableHead className="text-right">30 d</TableHead>
                  <TableHead className="text-right">Hendelser</TableHead>
                  <TableHead className="text-right">Henvendelser</TableHead>
                  <TableHead className="text-right">Brukere</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.customers.map((c) => (
                  <TableRow key={c.id} data-testid={`admin-customer-${c.key}`}>
                    <TableCell>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.contact_name} · {c.org_number}
                      </div>
                    </TableCell>
                    <TableCell className="metric text-right">{c.plants_total}</TableCell>
                    <TableCell className="metric text-right">
                      {c.plants_running}
                      {c.plants_stopped ? <span className="ml-1 text-xs text-rose-700 dark:text-rose-300">({c.plants_stopped} stanset)</span> : null}
                    </TableCell>
                    <TableCell className="metric text-right">{fmtMW(c.power_now_mw, 1)}</TableCell>
                    <TableCell className="metric text-right">{fmtInt(c.energy_today_mwh)} MWh</TableCell>
                    <TableCell className="metric text-right">{fmtMWh(c.energy_30d_mwh)}</TableCell>
                    <TableCell className="metric text-right">
                      {c.events_open}
                      {c.events_critical ? <span className="ml-1 text-xs text-rose-700 dark:text-rose-300">({c.events_critical} kritisk)</span> : null}
                    </TableCell>
                    <TableCell className="metric text-right">{c.service_requests_open}</TableCell>
                    <TableCell className="metric text-right">{c.users_active}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => viewAs(c)} data-testid={`admin-view-as-${c.key}`}>
                        Vis som kunde <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  );
}
